import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { com } from '@eidu/web'
import { useMemo } from 'react'
import ErrorIcon from '@mui/icons-material/Error'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import EqualityHashMap from '../../util/EqualityHashMap'
import PermissionPolicyId = com.eidu.sharedlib.entity.permission.PermissionPolicyId

type AppliedPoliciesListProps = {
  selectedPolicyIds: ReadonlyArray<PermissionPolicyId>
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
  loading?: boolean
  onRemove: (policyId: PermissionPolicyId) => void
}

const AppliedPoliciesList = ({ selectedPolicyIds, selectedPolicies, loading, onRemove }: AppliedPoliciesListProps) => {
  const policiesById = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, PermissionPolicyDTO>()
    selectedPolicies.forEach((p) => map.set(p.policyId, p))
    return map
  }, [selectedPolicies])

  if (selectedPolicyIds.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No policies applied.
        </Typography>
      </Box>
    )
  }

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ px: 2, pt: 1.5, pb: 0 }}>
      {selectedPolicyIds.map((id) => {
        const policy = policiesById.get(id)
        if (policy) {
          return <Chip key={id.toString()} label={policy.policy.name} onDelete={() => onRemove(id)} size="small" />
        }
        if (loading) {
          return <Skeleton key={id.toString()} variant="rounded" width={140} height={24} />
        }
        return (
          <Tooltip key={id.toString()} title={`Unknown policy: ${id.toString()}`}>
            <Chip
              color="warning"
              icon={<ErrorIcon fontSize="small" />}
              label="Unknown policy"
              onDelete={() => onRemove(id)}
              size="small"
            />
          </Tooltip>
        )
      })}
    </Stack>
  )
}

export default AppliedPoliciesList
